// src/components/Inventario/Reportes/ScrapAlmacenLaser/composables/useScrapAlmacenLaser.js
import { ref } from "vue";
import { getCatalogoProductos } from "@/utils/api_backend";
import { getScrapAlmacenResumen, getScrapAlmacenDefectosPareto } from "@/utils/api_scrap_almacen_laser";

const MESES = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];

/** 'YYYY-MM-DD' local, sin pasar por toISOString() (evita el corrimiento
 *  de zona horaria de convertir a UTC). */
function aISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Formatea una fecha ISO ('2026-01-05') a 'dd mes' parseando el string
 * directamente (evita el corrimiento de un día que da new Date('YYYY-MM-DD'),
 * que JS interpreta como UTC medianoche).
 */
function formatearDia(fechaIso) {
  const [, mes, dia] = fechaIso.split("-").map(Number);
  return `${dia} ${MESES[mes - 1]}`;
}

function etiquetaPeriodo(periodo) {
  if (!periodo?.fecha_inicio || !periodo?.fecha_fin) return "";
  const anio = periodo.fecha_fin.split("-")[0];
  return `${formatearDia(periodo.fecha_inicio)} – ${formatearDia(periodo.fecha_fin)} ${anio}`;
}

/**
 * Mismo criterio que el default anterior por semana: Periodo A = del 1 de
 * enero a la mitad de los días transcurridos del año; Periodo B = de esa
 * mitad+1 días hasta hoy.
 */
function periodoDefecto() {
  const hoy = new Date();
  const inicioAnio = new Date(hoy.getFullYear(), 0, 1);
  const diasTranscurridos = Math.floor((hoy - inicioAnio) / 86400000);
  const mitad = Math.max(1, Math.floor(diasTranscurridos / 2));

  const fechaMitad = new Date(inicioAnio);
  fechaMitad.setDate(fechaMitad.getDate() + mitad);

  const fechaMitadSiguiente = new Date(fechaMitad);
  fechaMitadSiguiente.setDate(fechaMitadSiguiente.getDate() + 1);

  // Si el año apenas empieza, la "mitad+1" puede caer después de hoy —
  // en ese caso Periodo B colapsa a un solo día (hoy) en vez de quedar invertido.
  const inicioB = fechaMitadSiguiente > hoy ? hoy : fechaMitadSiguiente;

  return {
    A: { fecha_inicio: aISO(inicioAnio), fecha_fin: aISO(fechaMitad) },
    B: { fecha_inicio: aISO(inicioB), fecha_fin: aISO(hoy) },
  };
}

export function useScrapAlmacenLaser() {
  const cargando          = ref(false);
  const cargandoProductos = ref(false);
  const error             = ref(null);

  const defaults = periodoDefecto();
  const periodoA              = ref({ ...defaults.A });
  const periodoB              = ref({ ...defaults.B });
  const productosSeleccionados = ref([]);
  const mostrarTodos          = ref(false);   // false = top 5, true = todos
  const catalogoProductos     = ref([]);

  const datosProductosA  = ref([]);
  const totalScrapA      = ref(0);
  const totalRealA       = ref(0);
  const porcentajeScrapA = ref(0);

  const datosProductosB  = ref([]);
  const totalScrapB      = ref(0);
  const totalRealB       = ref(0);
  const porcentajeScrapB = ref(0);

  // ── Pareto de defectos (Periodo A) — reacciona al selector de productos
  // de arriba, igual que la gráfica de barras; no tiene filtro propio. ──────
  const datosPareto           = ref([]);
  const cargandoPareto        = ref(false);

  function paramsProductos() {
    return productosSeleccionados.value.length
      ? productosSeleccionados.value.join(",")
      : "";
  }

  async function cargarCatalogoProductos() {
    cargandoProductos.value = true;
    try {
      catalogoProductos.value = await getCatalogoProductos();
    } catch (e) {
      console.error("Error catálogo productos:", e);
    } finally {
      cargandoProductos.value = false;
    }
  }

  async function cargarPareto() {
    cargandoPareto.value = true;
    try {
      datosPareto.value = await getScrapAlmacenDefectosPareto({
        fecha_inicio: periodoA.value.fecha_inicio,
        fecha_fin:    periodoA.value.fecha_fin,
        productos:    paramsProductos(),
      });
    } catch (e) {
      console.error("Error Pareto defectos:", e);
      datosPareto.value = [];
    } finally {
      cargandoPareto.value = false;
    }
  }

  async function cargarResumen() {
    cargando.value = true;
    error.value    = null;
    try {
      const productos = paramsProductos();
      const [dataA, dataB] = await Promise.all([
        getScrapAlmacenResumen({
          fecha_inicio: periodoA.value.fecha_inicio,
          fecha_fin:    periodoA.value.fecha_fin,
          productos,
        }),
        getScrapAlmacenResumen({
          fecha_inicio: periodoB.value.fecha_inicio,
          fecha_fin:    periodoB.value.fecha_fin,
          productos,
        }),
      ]);

      datosProductosA.value  = dataA.productos       ?? [];
      totalScrapA.value      = dataA.total_scrap      ?? 0;
      totalRealA.value       = dataA.total_real       ?? 0;
      porcentajeScrapA.value = dataA.porcentaje_scrap ?? 0;

      datosProductosB.value  = dataB.productos       ?? [];
      totalScrapB.value      = dataB.total_scrap      ?? 0;
      totalRealB.value       = dataB.total_real       ?? 0;
      porcentajeScrapB.value = dataB.porcentaje_scrap ?? 0;
    } catch (e) {
      error.value = "No se pudieron cargar los datos.";
      console.error("Error ScrapAlmacenLaser:", e);
    } finally {
      cargando.value = false;
    }
    // Recargar Pareto cuando cambia el periodo/productos
    cargarPareto();
  }

  async function cargarTodo() {
    await cargarCatalogoProductos();
    await Promise.all([cargarResumen(), cargarPareto()]);
  }

  function actualizarPeriodoA(nuevo) { periodoA.value = nuevo; cargarResumen(); }
  function actualizarPeriodoB(nuevo) { periodoB.value = nuevo; cargarResumen(); }
  function actualizarProductosSeleccionados(nuevos) {
    productosSeleccionados.value = nuevos;
    cargarResumen();
  }
  function actualizarMostrarTodos(valor) {
    mostrarTodos.value = valor;
    if (valor) productosSeleccionados.value = [];
  }

  return {
    cargando, cargandoProductos, error,
    periodoA, periodoB,
    productosSeleccionados, mostrarTodos, catalogoProductos,
    datosProductosA, totalScrapA, totalRealA, porcentajeScrapA,
    datosProductosB, totalScrapB, totalRealB, porcentajeScrapB,
    // Pareto
    datosPareto, cargandoPareto,
    etiquetaPeriodo, cargarTodo,
    actualizarPeriodoA, actualizarPeriodoB,
    actualizarProductosSeleccionados,
    actualizarMostrarTodos,
  };
}